import { isTtnApproved } from "~/shared/lib/shift-status";
import { ConnectProto } from "@racoon/api";
import { getJobFactVolume, getJobVolume } from "~/shared/lib/job-metrics";
import {
  getRouteScaleWeight,
  isShownOnTtn,
  nonZero,
  ts,
  type Draft,
  type FieldSource,
} from "./ttnDraftUtils";
import type { TripTimes } from "./useTtnDraft";
import { plannedContainerCount } from "~/shared/lib/job-containers";

/** Everything a TTN write needs, as it is on screen at the moment of "approve". */
export type TtnSnapshot = {
  shift: ConnectProto.DriverOnShift;
  /** The shift with the draft and the fallbacks applied. */
  shiftView: ConnectProto.DriverOnShift;
  routes: ConnectProto.Route[];
  /** The trips with the draft and the fallbacks applied. */
  routesView: ConnectProto.Route[];
  draft: Draft;
  /** Trip times as shown, with the GPS / scale values that won. */
  tripTimes: Map<bigint, TripTimes>;
};

// The driver's marks and the approved TTN are the route's stored fact times
// already; a plan is not a fact, and the approved TTN falls back to it anyway.
const isUnsaved = (source: FieldSource | undefined) =>
  source !== undefined && source !== "driver" && source !== "saved" && source !== "plan";

// Nothing to declare about a placeholder or about work that never ran.
const isCountedJob = (job: ConnectProto.Job) =>
  !job.isPseudo && job.status !== ConnectProto.JobStatus.CANCELLED;

/**
 * The trip's weight shared out over its jobs that declare none, by the volume
 * each took (equally when none is known), in whole kg that add up to the trip's.
 * A job's declared weight stands and comes off the total first. The TTN has no
 * per-job weight to type, yet the note needs one on every job.
 */
const splitTripWeight = (
  weightKg: number | undefined,
  jobs: ConnectProto.Job[],
): Map<bigint, number> => {
  const shares = new Map<bigint, number>();
  const open = jobs.filter((job) => isCountedJob(job) && job.weightKg == null);
  if (weightKg == null || open.length === 0) return shares;

  const declared = jobs
    .filter((job) => isCountedJob(job) && job.weightKg != null)
    .reduce((sum, job) => sum + (job.weightKg ?? 0), 0);
  const total = Math.max(0, Math.round(weightKg - declared));
  const volumes = open.map((job) => getJobFactVolume(job) ?? getJobVolume(job));
  const volumeSum = volumes.reduce((sum, volume) => sum + volume, 0);
  const exact = open.map((_, i) =>
    volumeSum > 0 ? (total * volumes[i]) / volumeSum : total / open.length,
  );

  // Largest remainder: floor every share, then hand the kg left over to the
  // shares that lost the most, so the parts sum to the trip's weight exactly.
  const floors = exact.map(Math.floor);
  let left = total - floors.reduce((sum, kg) => sum + kg, 0);
  const byRemainder = exact
    .map((kg, i) => [kg - floors[i], i] as const)
    .sort((a, b) => b[0] - a[0]);
  for (const [, i] of byRemainder) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  open.forEach((job, i) => shares.set(job.id, floors[i]));
  return shares;
};

/**
 * The note requests an approval sends: the whole on-screen TTN. Note RPCs are
 * partial, so a field left undefined keeps its stored value.
 */
export const buildTtnNotes = ({ shift, shiftView, routesView, draft, tripTimes }: TtnSnapshot) => {
  const ownerOrganizationId = shift.ownerOrganizationId;
  const isDone = isTtnApproved(shift);

  const shiftNote = {
    driverOnShiftId: shift.id,
    ownerOrganizationId,
    departureLocationId: shiftView.departureLocationId || undefined,
    arrivalLocationId: shiftView.arrivalLocationId || undefined,
    departureTime: shiftView.departureTime,
    arrivalTime: shiftView.arrivalTime,
    // A zero typed by the dispatcher is sent; a derived default of zero is not.
    engineHours: draft.shift.engineHours ?? nonZero(shiftView.engineHours),
    mileage: draft.shift.mileage ?? nonZero(shiftView.mileage),
  };

  // Every trip and job is written, pseudo ones included: a pseudo trip is one
  // the dispatcher entered by hand, which is real work for the TTN.
  const routeNotes = routesView.map((route) => {
    const patch = draft.routes.get(route.id) ?? {};
    // A cancelled trip is off the TTN: it keeps its times and place.
    const shown = isShownOnTtn(route) ? tripTimes.get(route.id) : undefined;
    const place = shown && (patch.destination?.id ?? shown.derivedPlace?.locationId);
    return {
      routeId: route.id,
      // An edit wins; else an open TTN takes the scale ticket, and an approved
      // one keeps its weight, so a scale ticket changed since can't replace it.
      weightKg: isDone
        ? (patch.weightKg ?? nonZero(route.weightKg))
        : (patch.weightKg ?? getRouteScaleWeight(route) ?? nonZero(route.weightKg)),
      // A typed zero is sent; a derived default of zero is not.
      volume: nonZero(route.volume),
      mileage: patch.mileage ?? nonZero(route.mileage),
      // Approving freezes the trip as shown: a time or place that is not
      // already the route's own (the driver's marks / the approved TTN)
      // is saved — typed, GPS or scale ticket; never the plan.
      // An edit is always saved, even one picked off the plan's value.
      factStartAt:
        shown && (patch.factStartAt || isUnsaved(shown.startSource)) ? ts(shown.start) : undefined,
      factEndAt:
        shown && (patch.factEndAt || isUnsaved(shown.endSource)) ? ts(shown.end) : undefined,
      destinationLocationId: place || undefined,
    };
  });

  const jobNotesByProvider = new Map<bigint, ConnectProto.JobConsignmentNote[]>();
  routesView.forEach((route, i) => {
    const weights = splitTripWeight(routeNotes[i].weightKg ?? route.weightKg, route.jobs);
    for (const job of route.jobs) {
      const notes = jobNotesByProvider.get(job.serviceProviderId) ?? [];
      // A declared value, zero included, is kept; an undeclared count defaults
      // to the container group's, an undeclared weight to its share of the trip's,
      // an undeclared fact volume to the TTN's "Факт. объем" (fills, else nominal).
      notes.push({
        jobId: job.id,
        numOfContainer:
          job.numOfContainer ?? nonZero(plannedContainerCount(job)),
        weightKg: job.weightKg ?? weights.get(job.id),
        factTotalVolume: job.factTotalVolume ?? nonZero(getJobFactVolume(job)),
      } as ConnectProto.JobConsignmentNote);
      jobNotesByProvider.set(job.serviceProviderId, notes);
    }
  });

  return { shiftNote, routeNotes, jobNotesByProvider };
};
